import React from 'react'
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  Checkbox,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Company } from 'models'
import { entityDisplayName } from 'utils/helpers'
import { ALL_ACCOUNTS_ID } from 'contexts/CompaniesContext'
import { theme } from 'utils/theme'
import type { SelectionSummary } from './types/selectionTypes'

type ChildrenMultiSelectProps = {
  childrenCompanies: Array<Company>
  displayedPendingValue: Array<Company>
  onSelectionChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Array<Company>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Company>,
  ) => void
  isChildrenLoading: boolean
  isChildSelectorDisabled: boolean
  selectionLabel: string
  selectionDisplayValue: string
  selectionPlaceholder: string
  selectionSummary: SelectionSummary
  limitTooltip: string
  shouldShowSelectionTooltip: boolean
  shouldShowLimitTooltip: boolean
  isChildDropdownOpen: boolean
  openDropdown: () => void
  closeDropdown: () => void
  getOptionDisabled: (option: Company) => boolean
  pendingAllAccountsSelection: boolean
  canSelectAllChildren: boolean
  onHoverChildInput: (isHovering: boolean) => void
  inputRef: React.RefObject<HTMLInputElement | null>
  allAccountsOption?: Company
  onInputChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void
  inputValue: string
}

const renderSummaryContent = (summary: SelectionSummary) => {
  if (summary.type === 'allAccounts') {
    return (
      <Typography variant="body2">{`${entityDisplayName(summary.company)} (famille)`}</Typography>
    )
  }

  if (summary.type === 'multiple') {
    return (
      <div className="flex flex-col">
        {summary.companies.map((company) => (
          <Typography
            key={company.id}
            variant="body2"
          >
            {entityDisplayName(company)}
          </Typography>
        ))}
      </div>
    )
  }

  if (summary.type === 'single') {
    return <Typography variant="body2">{entityDisplayName(summary.company)}</Typography>
  }

  return null
}

const ChildrenMultiSelect: React.FC<ChildrenMultiSelectProps> = ({
  childrenCompanies,
  displayedPendingValue,
  onSelectionChange,
  isChildrenLoading,
  isChildSelectorDisabled,
  selectionLabel,
  selectionDisplayValue,
  selectionPlaceholder,
  selectionSummary,
  limitTooltip,
  shouldShowSelectionTooltip,
  shouldShowLimitTooltip,
  isChildDropdownOpen,
  openDropdown,
  closeDropdown,
  getOptionDisabled,
  pendingAllAccountsSelection,
  canSelectAllChildren,
  onHoverChildInput,
  inputRef,
  onInputChange,
  inputValue,
}) => (
  <div className="flex items-center gap-3">
    <Tooltip
      title={limitTooltip}
      open={shouldShowLimitTooltip}
      disableHoverListener
    >
      <div className="ml-1">
        <Tooltip
          title={renderSummaryContent(selectionSummary)}
          arrow
          placement="bottom-start"
          open={shouldShowSelectionTooltip}
          disableHoverListener
        >
          <div>
            <Autocomplete
              multiple
              disableCloseOnSelect
              loading={isChildrenLoading}
              options={childrenCompanies}
              value={displayedPendingValue}
              onChange={onSelectionChange}
              onInputChange={onInputChange}
              inputValue={inputValue}
              disabled={isChildSelectorDisabled || (isChildrenLoading && !childrenCompanies.length)}
              getOptionLabel={(option: Company) => entityDisplayName(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionDisabled={getOptionDisabled}
              renderTags={() => (
                <span
                  className="ml-2 text-sm"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: selectionDisplayValue
                      ? theme.palette.text.primary
                      : theme.palette.text.disabled,
                  }}
                >
                  {selectionLabel}
                </span>
              )}
              open={isChildSelectorDisabled ? false : isChildDropdownOpen}
              onOpen={() => {
                if (!isChildSelectorDisabled) {
                  openDropdown()
                }
              }}
              onClose={closeDropdown}
              renderOption={(props, option, { selected }) => {
                const isAllAccounts = option.id === ALL_ACCOUNTS_ID
                const isAllAccountsSelectable = canSelectAllChildren
                const isAllAccountsDisabled = isAllAccounts && !isAllAccountsSelectable
                const isAllAccountsChecked = pendingAllAccountsSelection && isAllAccountsSelectable

                return (
                  <li
                    {...props}
                    className={`${props.className || ''} ${isAllAccountsDisabled ? 'opacity-50' : ''}`}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <Checkbox
                      checked={isAllAccounts ? isAllAccountsChecked : selected}
                      disabled={getOptionDisabled(option) || isAllAccountsDisabled}
                      style={{ marginRight: 8 }}
                    />
                    {isAllAccounts ? (
                      <Tooltip
                        title="Disponible si la famille contient 15 comptes max"
                        arrow
                      >
                        <span>{entityDisplayName(option)}</span>
                      </Tooltip>
                    ) : (
                      <span>{entityDisplayName(option)}</span>
                    )}
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isChildrenLoading ? (
                          <CircularProgress
                            color="inherit"
                            size={16}
                            style={{ marginRight: 8 }}
                          />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  placeholder={selectionPlaceholder}
                  onMouseEnter={() => onHoverChildInput(true)}
                  onMouseLeave={() => onHoverChildInput(false)}
                  inputRef={inputRef}
                />
              )}
              sx={{
                'width': 320,
                '& .MuiInputBase-root': {
                  height: 40,
                  alignItems: 'center',
                },
                '& .MuiAutocomplete-inputRoot': {
                  flexWrap: 'nowrap',
                },
                '& .MuiAutocomplete-input': {
                  minWidth: 0,
                  width: 0,
                  flexGrow: 1,
                },
                '& .MuiAutocomplete-tag, & .MuiAutocomplete-tag + input': {
                  minWidth: 0,
                },
              }}
            />
          </div>
        </Tooltip>
      </div>
    </Tooltip>
  </div>
)

export { ChildrenMultiSelect }
